// A vendor bill entered without an order or a recorded delivery behind it.
import type { BillLinePayload, BillPayload } from '../components/MaterialReceivingDialogs';
import type { MaterialPick } from './materialReceiving';
import { roundMoney } from './materialReceiving';

export type DirectReason = 'notRecorded' | 'service';
export type DirectMode = 'lines' | 'total';

export const DIRECT_REASON_LABEL: Record<DirectReason, string> = {
  notRecorded: 'Delivered, but never recorded',
  service: 'Service or charges',
};

/** One line. A material line has a library pick or, when the material is not in the library, a typed name (description) and unit. */
export type DirectLineInput = { pickKey: string; description: string; qty: string; rate: string; amount: string; unit: string; gst: string };
export type DirectBillInput = {
  reason: DirectReason; mode: DirectMode; vendor: string; invoiceNumber: string; invoiceDate: string;
  /** What a total-only bill is for. */
  description: string; lines: DirectLineInput[];
  /** Whether the rates on the bill include GST. */
  gstMode: 'incl' | 'excl';
  /** Freight as printed on the bill, and the GST % to add on top of it (blank when the amount already includes GST). */
  freight: string; freightGst: string;
  roundOff: string; invoiceTotal: string; mismatchReason: string; notes: string;
};
export type DirectBillPayload = Omit<BillPayload, 'libraryUpdates'> & {
  sourceType: 'direct'; directReason: DirectReason; directMode: DirectMode; gstMode: 'incl' | 'excl';
  freightEntered?: number; freightGstPercent?: number;
};

const num = (value: unknown) => { const n = parseFloat(String(value)); return Number.isFinite(n) ? n : 0; };

export const emptyDirectLine = (): DirectLineInput => ({ pickKey: '', description: '', qty: '', rate: '', amount: '', unit: '', gst: '' });

/** The GST % on a material's latest rate-library quote, or 18 when it has none. */
export function defaultGstFor(pick?: MaterialPick): number {
  const quotes = (pick?.item.quotes || []).filter((quote) => (quote.status || 'active') !== 'voided');
  const gst = num(quotes[quotes.length - 1]?.gst);
  return gst > 0 ? gst : 18;
}

/** The lines as they will be saved, the totals, and what is still missing before the bill can be saved. */
export function planDirectBill(input: DirectBillInput, picks: MaterialPick[]) {
  const totalOnly = input.mode === 'total';
  const materialLines = input.reason === 'notRecorded';
  const lines: BillLinePayload[] = [];
  const missing: string[] = [];

  if (totalOnly) {
    const amount = roundMoney(num(input.invoiceTotal));
    if (amount > 0 && input.description.trim()) lines.push(chargeLine(input.description.trim(), amount));
  } else {
    input.lines.forEach((line) => {
      if (materialLines) {
        const pick = picks.find((candidate) => candidate.key === line.pickKey);
        const name = pick ? pick.label : line.description.trim();
        const unit = pick ? pick.unit : (line.unit.trim() || 'Nos');
        const qty = num(line.qty); const rate = num(line.rate);
        if (name && qty > 0 && rate > 0) {
          const excl = input.gstMode === 'excl';
          const gst = line.gst.trim() === '' ? defaultGstFor(pick) : num(line.gst);
          const rateIncl = excl ? rate * (1 + gst / 100) : rate;
          lines.push({
            orderId: '', receiptId: '', materialId: pick ? pick.item.id : '', quoteId: '', quoteOptionId: pick?.optionId || '',
            materialLabel: name, unit, qty, rate: roundMoney(rateIncl), amount: roundMoney(qty * rateIncl),
            enteredRate: rate, enteredUnit: unit, rateSource: 'Entered from bill',
            enteredGstMode: excl ? 'excl' : 'incl', ...(excl ? { gstPercent: gst } : {}), ...(pick ? {} : { unmapped: true }),
          });
        }
      } else if (line.description.trim() && num(line.amount) > 0) {
        lines.push(chargeLine(line.description.trim(), roundMoney(num(line.amount))));
      }
    });
  }

  const subtotal = roundMoney(lines.reduce((sum, line) => sum + line.amount, 0));
  const freightEntered = totalOnly ? 0 : num(input.freight);
  const freightGst = num(input.freightGst);
  const freight = roundMoney(freightEntered * (1 + freightGst / 100));
  const beforeRound = subtotal + freight;
  const autoRound = totalOnly ? 0 : roundMoney(Math.round(beforeRound) - beforeRound);
  const roundOff = totalOnly ? 0 : (input.roundOff.trim() === '' ? autoRound : num(input.roundOff));
  const calculatedTotal = totalOnly ? roundMoney(num(input.invoiceTotal)) : roundMoney(beforeRound + roundOff);
  const invoiceTotal = num(input.invoiceTotal);
  const difference = roundMoney(calculatedTotal - invoiceTotal);
  const mismatch = !totalOnly && invoiceTotal > 0 && Math.abs(difference) > 1;

  if (!input.vendor.trim()) missing.push('vendor');
  if (!input.invoiceNumber.trim()) missing.push('bill number');
  if (!input.invoiceDate) missing.push('bill date');
  if (totalOnly) { if (!input.description.trim()) missing.push('what the bill is for'); }
  else if (!lines.length) missing.push(materialLines ? 'at least one material with quantity and rate' : 'at least one charge with a description and amount');
  else if (lines.length < input.lines.filter((line) => line.pickKey || line.description.trim() || line.qty || line.rate || line.amount).length) missing.push('every line completed or removed');
  if (invoiceTotal <= 0) missing.push('bill total');
  if (mismatch && !input.mismatchReason.trim()) missing.push('a reason for the total difference');

  const payload: DirectBillPayload = {
    sourceType: 'direct', directReason: input.reason, directMode: input.mode,
    invoiceNumber: input.invoiceNumber.trim(), invoiceDate: input.invoiceDate, vendor: input.vendor.trim(),
    freight, roundOff, invoiceTotal, calculatedTotal, mismatchReason: mismatch ? input.mismatchReason.trim() : '', notes: input.notes.trim(), lines,
    gstMode: totalOnly ? 'incl' : input.gstMode,
    ...(freightEntered > 0 && freightGst > 0 ? { freightEntered, freightGstPercent: freightGst } : {}),
  };
  return { payload, lines, subtotal, autoRound, roundOff, calculatedTotal, difference, mismatch, missing };
}

function chargeLine(description: string, amount: number): BillLinePayload {
  return { orderId: '', receiptId: '', materialId: '', quoteId: '', quoteOptionId: '', materialLabel: description, unit: '', qty: 1, rate: amount, amount, enteredRate: amount, enteredUnit: '', rateSource: 'Entered from bill' };
}
